import type {
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DragStart,
  DragUpdate,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
  ResponderProvided,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration'
import {
  Droppable as DndDroppable,
  DragDropContext,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration'
import { Grip } from '@procore/core-icons/dist'
import { useLabel } from '@react-aria/label'
import { useId } from '@react-aria/utils'
import React, { isValidElement, useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import type { CheckboxProps } from '../Checkbox'
import { Checkbox, FakeCheckbox } from '../Checkbox/Checkbox'
import type { TypeaheadProps } from '../Typeahead'
import { Typeahead } from '../Typeahead'
import { useI18nContext } from '../_hooks/I18n'
import { useZIndexContext } from '../_hooks/ZIndex'
import { colors } from '../_styles/colors'
import { addSubcomponents } from '../_utils/addSubcomponents'
import { mergeRefs } from '../_utils/mergeRefs'
import type { PolymorphicAs } from '../_utils/polymorphic'
import {
  StyledDroppable,
  StyledFooter,
  StyledGrip,
  StyledGroup,
  StyledGroupLabel,
  StyledHeader,
  StyledItem,
  StyledMenu,
  StyledOptions,
  StyledSearch,
  StyledWrapper,
} from './MenuImperative.styles'
import type {
  ControlNavigationProps,
  ControlNavigationReturn,
  DroppableProps,
  FooterProps,
  GroupProps,
  HeaderProps,
  MenuContext as IMenuContext,
  ItemProps,
  ItemWithDraggingProps,
  MenuItemDefaultElement,
  MenuItemProps,
  MenuProps,
  MenuRef,
  MenuSearchProps,
  OptionsProps,
  Selection,
} from './MenuImperative.types'
import { createSensors } from './sensors'

const onScrollBottomThreshold = 8

/**
 * Finds the group element for a given droppable ID (from DnD result).
 * Locates the Droppable via data-droppable-id, then returns its parent Group
 * (ancestor with data-group="true") so we can read data-title for announcements.
 */
function findGroupByDroppableId(
  container: HTMLElement | null,
  droppableId: string
): HTMLElement | null {
  const droppable = container?.querySelector<HTMLElement>(
    `[data-droppable-id="${droppableId}"]`
  )
  if (!droppable) return null
  const group = droppable.closest<HTMLElement>('[data-group="true"]')
  return group ?? null
}

function noop() {}

function isScrolledToBottom(e: React.UIEvent<HTMLDivElement, UIEvent>) {
  return (
    e.currentTarget instanceof HTMLElement &&
    e.currentTarget.scrollTop >=
      e.currentTarget.scrollHeight -
        e.currentTarget.clientHeight -
        onScrollBottomThreshold
  )
}

export const MenuContext = React.createContext<IMenuContext>({
  a11yOptionsProps: {},
  currentlyDroppableIn: [],
  dndInstructionsId: '',
  onHoverItem: noop,
  onFocusItem: noop,
  onBlurItem: noop,
  onMouseLeaveItems: noop,
  onScrollBottom: noop,
  onSelect: noop,
  role: 'none',
})

export function useMenuContext(): IMenuContext {
  const context = React.useContext(MenuContext)

  if (context === null) {
    throw new Error(
      'Cannot find `Menu` context, please wrap your component in `<MenuContext.Provider>`'
    )
  }

  return context
}

const IsInsideDroppableContext = React.createContext(false)

function useIsInsideDroppable(): boolean {
  return React.useContext(IsInsideDroppableContext)
}

/**
 * Usefully if navigation is controlled by search input or another input.
 *
 * @param ref MenuImperative ref
 * @param enable True by default. Setting to false will change all props
 * to undefined (except id in menuProps), so navigation controlled by menu itself is restored.
 * @param options.menuId provide id otherwise it will be generated
 * @since 11.5.0
 */
export const useMenuImperativeControlNavigation = (
  ref: React.RefObject<MenuRef>,
  enable = true,
  options: ControlNavigationProps = {}
): ControlNavigationReturn => {
  const { menuId: _id } = options
  const id = useId(_id)
  const [activeDescendant, setActiveDescendant] = React.useState<
    string | undefined
  >()
  const [onKeyDown, setOnKeyDown] = useState<React.KeyboardEventHandler>()
  // ref is null on first render
  React.useEffect(() => {
    setOnKeyDown(() => ref.current?.defaultKeyDownNavigationHandler)
  }, [])

  if (enable) {
    const menuNavigationTriggerProps = {
      'aria-activedescendant': activeDescendant,
      'aria-controls': id,
      onKeyDown,
    }

    const menuProps = {
      onChangeActiveDescendant: setActiveDescendant,
      id,
    }

    return {
      menuNavigationTriggerProps,
      menuProps,
    }
  } else {
    return {
      menuNavigationTriggerProps: {},
      menuProps: { id },
    }
  }
}

function changeAriaActiveDescendantOnOptions(id = '', menuRef: Element | null) {
  const options = menuRef?.querySelector<HTMLElement>(
    '[data-internal="menuimperative-options"]'
  )

  options?.setAttribute('aria-activedescendant', id)
}

const MenuImperative_ = React.forwardRef<MenuRef, MenuProps>(function Menu(
  {
    circular = false,
    onDragEnd: onDragEnd_ = noop,
    onScrollBottom: onScrollBottom_ = noop,
    onSelect: onSelect_ = noop,
    role = 'none',
    onChangeActiveDescendant,
    onKeyboardNavigation: onKeyboardNavigation_,
    id,
    multiple,
    ...props
  },
  forwardRef
) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [currentlyDroppableIn, setCurrentlyDroppableIn] = useState<string[]>([])
  const i18n = useI18nContext()
  const dndInstructionsId = useId()

  // for context, used by Menu.Item
  const onSelect = React.useCallback(
    function (selection: Selection) {
      onSelect_(selection)
    },
    [onSelect_]
  )

  const onChangeHighlight = onChangeActiveDescendant
    ? onChangeActiveDescendant
    : changeAriaActiveDescendantOnOptions

  const {
    handleItemHover,
    handleItemFocus,
    handleItemBlur,
    handleItemsMouseLeave,
    handleKeyDown: defaultKeyDownNavigationHandler,
    highlight,
    highlighted,
    highlightFirst,
    highlightLast,
    highlightSelected,
    highlightSuggested,
    rehighlightCurrent,
    prev,
    next,
    select,
    updateSelectCallback,
    setIsDragging,
  } = useMemo(
    () => createSensors(ref, circular, onChangeHighlight),
    [circular, onChangeHighlight]
  )

  const commonA11yOptionProps = {
    role,
    id,
    ['aria-multiselectable']: multiple,
  }

  const a11yOptionsProps = !onChangeActiveDescendant
    ? {
        ...commonA11yOptionProps,
        onKeyDown: onKeyboardNavigation_ ?? defaultKeyDownNavigationHandler,
        tabIndex: 0,
      }
    : {
        ...commonA11yOptionProps,
        onKeyDown: undefined,
        tabIndex: undefined,
      }

  updateSelectCallback(onSelect)

  // exposes internal menu API
  React.useImperativeHandle(forwardRef, () => ({
    el: ref.current,
    highlight,
    highlighted,
    highlightFirst,
    highlightLast,
    highlightSelected,
    highlightSuggested,
    next,
    prev,
    select,
    defaultKeyDownNavigationHandler,
  }))

  useEffect(rehighlightCurrent)

  function onDragStart(start: DragStart, provided: ResponderProvided) {
    const itemInfo = JSON.parse(start.draggableId)
    // flushSync needed to ensure proper behavior in Safari
    flushSync(() => setCurrentlyDroppableIn(itemInfo.droppableIn))
    setIsDragging(true)

    const startPosition = start.source.index + 1
    const startGroupTitle =
      findGroupByDroppableId(
        ref.current,
        start.source.droppableId
      )?.getAttribute('data-title') ?? ''

    const announcement = startGroupTitle
      ? i18n.t('core.menuImperative.dragStartWithGroup', {
          startPosition,
          startGroupTitle,
        })
      : i18n.t('core.menuImperative.dragStart', {
          startPosition,
        })
    provided.announce(announcement)
  }

  const onDragUpdate = React.useCallback(function (
    _update: DragUpdate,
    provided: ResponderProvided
  ) {
    const startPosition = _update.source.index + 1
    const startGroupTitle =
      findGroupByDroppableId(
        ref.current,
        _update.source.droppableId
      )?.getAttribute('data-title') ?? ''

    const endPosition = _update.destination?.index + 1
    const endGroupTitle = _update.destination
      ? findGroupByDroppableId(
          ref.current,
          _update.destination.droppableId
        )?.getAttribute('data-title') ?? ''
      : ''

    const announcement =
      startGroupTitle === endGroupTitle || !endGroupTitle || !startGroupTitle
        ? i18n.t('core.menuImperative.dragUpdate', {
            startPosition,
            endPosition,
          })
        : i18n.t('core.menuImperative.dragUpdateWithGroup', {
            startPosition,
            endPosition,
            startGroupTitle,
            endGroupTitle,
          })
    provided.announce(announcement)
  },
  [])

  const onDragEnd = React.useCallback(
    function (result: DropResult, provided: ResponderProvided) {
      const endPosition = result.destination?.index + 1
      const endGroupTitle = result.destination
        ? findGroupByDroppableId(
            ref.current,
            result.destination.droppableId
          )?.getAttribute('data-title') ?? ''
        : ''

      const announcement = endGroupTitle
        ? i18n.t('core.menuImperative.dragEndWithGroup', {
            endPosition,
            endGroupTitle,
          })
        : i18n.t('core.menuImperative.dragEnd', {
            endPosition,
          })
      provided.announce(announcement)
      setCurrentlyDroppableIn([])
      setIsDragging(false)
      if (result.reason === 'DROP' && result.destination) {
        const itemInfo = JSON.parse(result.draggableId)
        onDragEnd_({
          draggableId: itemInfo.draggableId,
          source: result.source,
          destination: result.destination,
        })
      }
    },
    [onDragEnd_, setIsDragging]
  )

  // for context, used by Menu.Options
  const onScrollBottom = React.useCallback(
    function (e: React.UIEvent<HTMLDivElement, UIEvent>) {
      if (isScrolledToBottom(e)) {
        onScrollBottom_(e)
      }
    },
    [onScrollBottom_]
  )

  return (
    <MenuContext.Provider
      value={{
        a11yOptionsProps,
        currentlyDroppableIn,
        dndInstructionsId,
        onHoverItem: handleItemHover,
        onFocusItem: handleItemFocus,
        onBlurItem: handleItemBlur,
        onMouseLeaveItems: handleItemsMouseLeave,
        onScrollBottom,
        onSelect,
        role,
      }}
    >
      <StyledWrapper ref={ref}>
        <DragDropContext
          onDragStart={onDragStart}
          onDragUpdate={onDragUpdate}
          onDragEnd={onDragEnd}
        >
          <StyledMenu {...props} />
        </DragDropContext>
      </StyledWrapper>
      <div id={dndInstructionsId} hidden>
        {i18n.t('core.menuImperative.dragAndDropInstructions')}
      </div>
    </MenuContext.Provider>
  )
})

export const Group = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & GroupProps
>(function Group(
  { clickable, item = 'group', label, labelId, children, id, ...props },
  ref
) {
  let { fieldProps, labelProps } = useLabel({
    id,
    label: label ?? children,
    labelElementType: 'span',
  })

  if (labelId) {
    fieldProps = {
      ...fieldProps,
      'aria-labelledby': labelId,
    }
    labelProps = {
      ...labelProps,
      id: labelId,
    }
  }

  return (
    <StyledGroup
      ref={ref}
      data-group={true}
      data-value={JSON.stringify(item)}
      data-title={label}
      role={label ? 'group' : 'separator'}
      {...fieldProps}
      {...props}
    >
      <StyledGroupLabel {...labelProps}>{label ?? children}</StyledGroupLabel>
      {label ? children : null}
    </StyledGroup>
  )
})

function findDraggableRecursively(
  children: React.ReactNode,
  draggableId: any
): React.ReactElement | null {
  const nodeChildren = React.Children.toArray(children)
  for (let i = 0; i < nodeChildren.length; i++) {
    const child = nodeChildren[i]
    if (isValidElement(child)) {
      if ('draggableId' in child.props) {
        if (child.props.draggableId === draggableId) {
          return child
        }
      } else {
        const matchingChild = findDraggableRecursively(
          child.props.children,
          draggableId
        )
        if (matchingChild) {
          return matchingChild
        }
      }
    }
  }
  return null
}

export function Droppable({ id, children, ...props }: DroppableProps) {
  const ctx = useMenuContext()
  const cloneZIndex = useZIndexContext().value

  function renderItemClone(
    provided: DraggableProvided,
    _snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric
  ) {
    const draggableInfo = JSON.parse(rubric.draggableId)
    const draggingChild = findDraggableRecursively(
      children,
      draggableInfo.draggableId
    )
    const cloneProps = draggingChild?.props ?? {}

    return (
      <ClonedItem
        hasCheckbox={draggingChild?.type === CheckboxItem}
        provided={provided}
        cloneZIndex={cloneZIndex}
        dndInstructionsId={ctx.dndInstructionsId}
        {...cloneProps}
      />
    )
  }

  return (
    <DndDroppable
      droppableId={id}
      isDropDisabled={!ctx.currentlyDroppableIn.includes(id)}
      renderClone={renderItemClone}
    >
      {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
        <StyledDroppable
          ref={provided.innerRef}
          data-droppable
          data-droppable-id={id}
          {...props}
        >
          <IsInsideDroppableContext.Provider value={true}>
            {children}
            {provided.placeholder}
          </IsInsideDroppableContext.Provider>
        </StyledDroppable>
      )}
    </DndDroppable>
  )
}

// creates a version of a draggable item that can be positioned correctly within modals
// this should always have the same appearance of an Item or CheckboxItem, but doesn't
// need to handle selection events, which can't be done while the item is being dragged
const ClonedItem = ({
  hasCheckbox = false,
  children = null,
  selected = false,
  provided,
  dndInstructionsId,
  ...props
}: React.HTMLAttributes<HTMLDivElement> &
  ItemProps &
  Omit<CheckboxProps, 'checked'> & {
    provided: DraggableProvided
    hasCheckbox: boolean
    cloneZIndex: number
    dndInstructionsId: string
  }) => {
  const itemProps = {
    ...props,
    'aria-describedby': dndInstructionsId,
    $selected: selected,
  }

  const itemChildren = hasCheckbox ? (
    <Checkbox
      indeterminate={props.indeterminate}
      error={props.error}
      checked={selected}
      onChange={noop}
    >
      {children}
    </Checkbox>
  ) : (
    children
  )

  return (
    <StyledItem
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      {...itemProps}
      $isDraggable
      $isDragging
      style={{
        ...provided.draggableProps.style,
        ...props.style,
        zIndex: props.cloneZIndex,
      }}
    >
      <StyledGrip>
        <Grip color={colors.gray45} />
      </StyledGrip>
      {itemChildren}
    </StyledItem>
  )
}

export const Item = React.forwardRef(function Item<
  E extends React.ElementType = MenuItemDefaultElement
>(
  {
    as,
    /* @ts-ignore */
    children = null,
    item = null,
    /* @ts-ignore */
    onClick: onClick_ = noop,
    selected = false,
    suggested = false,
    disabled = false,
    id: _id,
    role,
    restoreFocus,
    draggableId,
    draggableIndex,
    droppableIn,
    ...props
  }: PolymorphicAs.ComponentPropsWithoutRef<MenuItemProps, E>,
  /**
   * TODO: although it is typed just fine from the outside (`<Menu.Item ref>` that is),
   *  an effort is required to type it properly here too
   */
  forwardRef: React.Ref<any>
) {
  const id = useId(_id)

  const ctx = useMenuContext()
  const isInsideDroppable = useIsInsideDroppable()

  const onClick = React.useCallback(
    function (event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
      if (disabled) return

      onClick_(event)

      ctx.onSelect({
        item,
        event,
        group: false,
        action: selected ? 'unselected' : 'selected',
        ...(restoreFocus === undefined ? {} : { restoreFocus }),
      })
    },
    [ctx, item, onClick_, disabled, selected]
  )

  let contextRole
  let ariaSelected

  if (ctx.role === 'listbox') {
    contextRole = 'option'
  } else if (ctx.role === 'menu') {
    contextRole = 'menuitem'
  }

  if ([role, contextRole].includes('option')) {
    ariaSelected = selected ? true : undefined
  }

  const ariaDescribedBy = isInsideDroppable ? ctx.dndInstructionsId : undefined

  const itemProps = {
    'aria-disabled': disabled ? true : undefined,
    'aria-describedby': ariaDescribedBy,
    ...props,
    'aria-selected': ariaSelected,
    id,
    role: role ?? contextRole,
    onClick,
    onMouseMove: (e: any) => ctx.onHoverItem(e.currentTarget),
    onFocus: (e: any) => ctx.onFocusItem(e.currentTarget),
    onBlur: (e: any) => ctx.onBlurItem(e.currentTarget),
    'data-group': false,
    'data-value': JSON.stringify(item),
    'data-selected': selected,
    'data-suggested': suggested,
    'data-disabled': disabled,
    'data-restorefocus': restoreFocus,
    $disabled: disabled,
    $selected: selected,
  }

  if (
    droppableIn !== undefined &&
    draggableId !== undefined &&
    draggableIndex !== undefined
  ) {
    // encoding id and valid droppables as the draggableId, so we can use them to determine where this item can be dropped
    const droppableInfo = {
      draggableId,
      droppableIn: Array.isArray(droppableIn) ? droppableIn : [droppableIn],
    }
    return (
      <Draggable
        draggableId={JSON.stringify(droppableInfo)}
        index={draggableIndex}
        isDragDisabled={disabled}
      >
        {(provided: DraggableProvided) => (
          <StyledItem
            ref={mergeRefs(provided.innerRef, forwardRef)}
            as={as}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            {...itemProps}
            $isDraggable={!disabled}
            style={{ ...provided.draggableProps.style, ...itemProps.style }}
          >
            {!disabled && (
              <StyledGrip>
                <Grip color={colors.gray45} />
              </StyledGrip>
            )}
            {children}
          </StyledItem>
        )}
      </Draggable>
    )
  }

  return (
    <StyledItem ref={forwardRef} as={as} {...itemProps}>
      {children}
    </StyledItem>
  )
}) as PolymorphicAs.ComponentWithForwardedRef<
  MenuItemProps,
  MenuItemDefaultElement
>

export const CheckboxItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    (ItemProps | ItemWithDraggingProps) &
    Omit<CheckboxProps, 'checked'>
>(function CheckboxItem(
  {
    selected = false,
    disabled = false,
    error = false,
    indeterminate = false,
    children = null,
    role,
    ...props
  },
  forwardRef
) {
  const ctx = useMenuContext()

  let contextRole
  let ariaChecked: React.AriaAttributes['aria-checked']
  let ariaSelected

  if (ctx.role === 'listbox') {
    contextRole = 'option'
    ariaSelected = selected ? true : undefined
  } else if (ctx.role === 'menu') {
    contextRole = 'menuitemcheckbox'

    if (indeterminate) {
      ariaChecked = 'mixed'
    } else {
      ariaChecked = selected ? 'true' : 'false'
    }
  }

  return (
    <Item
      aria-checked={ariaChecked}
      aria-disabled={disabled ? true : undefined}
      aria-selected={ariaSelected}
      {...props}
      role={role ?? contextRole}
      selected={selected}
      disabled={disabled}
      ref={forwardRef}
    >
      <FakeCheckbox
        indeterminate={indeterminate}
        error={error}
        disabled={disabled}
        checked={selected}
      >
        {children}
      </FakeCheckbox>
    </Item>
  )
})
export const Options = React.forwardRef<HTMLDivElement, OptionsProps>(
  function Options(props, ref) {
    const ctx = useMenuContext()
    const i18n = useI18nContext()

    return (
      <StyledOptions
        aria-label={i18n.t('core.menuImperative.options.ariaLabel')}
        {...props}
        {...ctx.a11yOptionsProps}
        // @ts-expect-error - JavaScript, Core-React use only for Tab
        role={props.role ?? ctx.a11yOptionsProps.role}
        data-internal="menuimperative-options"
        ref={ref}
        onScroll={ctx.onScrollBottom}
        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
          props.onMouseLeave?.(e)
          ctx.onMouseLeaveItems()
        }}
      />
    )
  }
)

export const Search = React.forwardRef<HTMLInputElement, MenuSearchProps>(
  function Search(
    {
      className,
      i18nScope = 'core.menu',
      placeholder,
      onChange: _onChange,
      value: valueProp,
      ...props
    },
    ref
  ) {
    const I18n = useI18nContext()

    const isControlled = valueProp !== undefined
    const [internalValue, setInternalValue] = React.useState('')
    const value = isControlled ? valueProp : internalValue

    const onChange: NonNullable<TypeaheadProps['onChange']> = React.useCallback(
      function (value, event) {
        if (!isControlled) {
          setInternalValue(value)
        }
        _onChange?.(event as unknown as React.ChangeEvent<HTMLInputElement>)
      },
      [_onChange, isControlled]
    )

    return (
      <StyledSearch className={className}>
        <Typeahead
          aria-label={I18n.t('searchLabel', { scope: i18nScope })}
          {...props}
          ref={ref}
          type="text"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={true}
          autoFocus={true}
          placeholder={placeholder || I18n.t('search', { scope: i18nScope })}
          onChange={onChange}
          value={value}
        />
      </StyledSearch>
    )
  }
)

export const Header = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & HeaderProps
>(function Header({ ...props }, ref) {
  return <StyledHeader ref={ref} {...props} />
})

export const Footer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & FooterProps
>(function Footer({ padding = 'md lg', ...props }, ref) {
  return <StyledFooter ref={ref} padding={padding} {...props} />
})

MenuImperative_.displayName = 'Menu'

Droppable.displayName = 'Menu.Droppable'

Footer.displayName = 'Menu.Footer'

Group.displayName = 'Menu.Group'

Header.displayName = 'Menu.Header'

Item.displayName = 'Menu.Item'

Options.displayName = 'Menu.Options'

Search.displayName = 'Menu.Search'

/**

   @since 10.19.0

   @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-menu-imperative--demo)

 */
export const MenuImperative = addSubcomponents(
  {
    CheckboxItem,
    Droppable,
    Footer,
    Group,
    Header,
    Item,
    Options,
    Search,
  },
  MenuImperative_
)
